import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  MenuItem,
  IconButton,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Select,
  Alert,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DateInputWithArrows from '../components/DateInputWithArrows';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Visibility as ViewIcon 
} from '@mui/icons-material';
import { 
  getWriteOffs, 
  createWriteOff, 
  updateWriteOff, 
  deleteWriteOff,
  getAvailableToolsForWriteOff,
  getAvailableMaterialEquipmentForWriteOff,
} from '../services/api';
import { formatDateEuropean } from '../utils/dateUtils';
import useResponsive from '../components/responsive/useResponsive';
import ResponsiveDataList from '../components/responsive/ResponsiveDataList';
import ResponsivePageSection from '../components/responsive/ResponsivePageSection';
import { useTranslation } from 'react-i18next';

const ITEM_KINDS = ['Material', 'Machine', 'Equipment', 'Tool'];

function itemKindLabel(kind, t) {
  return t(`warehouse.itemCategory.${kind}`, { defaultValue: kind });
}

function warehouseStockQty(item) {
  if (!item) return 0;
  if (String(item.type || '').toLowerCase() === 'machine') return 1;
  return item.quantity == null ? 0 : Number(item.quantity);
}

function writeOffItemKind(writeOff) {
  if (!writeOff) return '';
  if (writeOff.itemType === 'TOOL' || writeOff.tool) return 'Tool';
  const type = writeOff.materialEquipment?.type;
  if (ITEM_KINDS.includes(type)) return type;
  return 'Material';
}

function emptyForm() {
  return {
    itemKind: '',
    toolId: '',
    materialEquipmentId: '',
    quantity: '1',
    reason: '',
    writeOffDate: new Date().toISOString().split('T')[0],
    notes: '',
  };
}

function WriteOffs() {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const [writeOffs, setWriteOffs] = useState([]);
  const [availableTools, setAvailableTools] = useState([]);
  const [availableMaterialEquipment, setAvailableMaterialEquipment] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingWriteOff, setEditingWriteOff] = useState(null);
  const [selectedWriteOff, setSelectedWriteOff] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [error, setError] = useState('');

  const columns = [
    { 
      field: 'itemType', 
      headerName: t('writeOffs.itemType'), 
      width: 150,
      renderCell: (params) => {
        const kind = writeOffItemKind(params.row);
        return (
          <Chip 
            label={itemKindLabel(kind, t)} 
            color={kind === 'Tool' ? 'primary' : 'secondary'}
            size="small"
          />
        );
      }
    },
    { 
      field: 'itemName', 
      headerName: t('writeOffs.item'), 
      width: 200,
      valueGetter: (params) => {
        if (params.row.tool) return params.row.tool.name;
        if (params.row.materialEquipment) return params.row.materialEquipment.name;
        return 'N/A';
      }
    },
    { field: 'reason', headerName: t('writeOffs.reason'), width: 200 },
    { field: 'writeOffBy', headerName: t('writeOffs.writeOffBy'), width: 150 },
    { 
      field: 'writeOffDate',
      headerName: t('writeOffs.writeOffDate'),
      width: 150,
      valueFormatter: (params) => formatDateEuropean(params.value),
    },
    {
      field: 'actions',
      headerName: t('common.actions'),
      width: 200,
      renderCell: (params) => (
        <Box>
          <Tooltip title={t('writeOffs.viewDetails')}>
            <IconButton onClick={() => handleView(params.row)} color="info" size="small">
              <ViewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.edit')}>
            <IconButton onClick={() => handleEdit(params.row)} color="primary" size="small">
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <IconButton onClick={() => handleDelete(params.row.id)} color="error" size="small">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  useEffect(() => {
    fetchWriteOffs();
    fetchAvailableItems();
  }, []);

  const fetchWriteOffs = async () => {
    try {
      setLoading(true);
      const response = await getWriteOffs();
      setWriteOffs(response.data);
    } catch (error) {
      console.error('Error fetching write-offs:', error);
      setError(t('writeOffs.failedFetch'));
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableItems = async () => {
    try {
      const [toolsResponse, materialResponse] = await Promise.all([
        getAvailableToolsForWriteOff(),
        getAvailableMaterialEquipmentForWriteOff()
      ]);
      setAvailableTools(toolsResponse.data);
      setAvailableMaterialEquipment(materialResponse.data);
    } catch (error) {
      console.error('Error fetching available items:', error);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setEditingWriteOff(null);
    setFormData(emptyForm());
    setError('');
  };

  const handleClose = () => {
    setOpen(false);
    setEditingWriteOff(null);
    setFormData(emptyForm());
    setError('');
  };

  const handleView = (writeOff) => {
    setSelectedWriteOff(writeOff);
    setViewOpen(true);
  };

  const handleViewClose = () => {
    setViewOpen(false);
    setSelectedWriteOff(null);
  };

  const handleEdit = (writeOff) => {
    setEditingWriteOff(writeOff);
    setFormData({
      itemKind: writeOffItemKind(writeOff),
      toolId: writeOff.toolId || '',
      materialEquipmentId: writeOff.materialEquipmentId || '',
      quantity: writeOff.quantity != null ? String(writeOff.quantity) : '1',
      reason: writeOff.reason,
      writeOffDate: new Date(writeOff.writeOffDate).toISOString().split('T')[0],
      notes: writeOff.notes || '',
    });
    setOpen(true);
    setError('');
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('writeOffs.deleteConfirm'))) {
      try {
        await deleteWriteOff(id);
        fetchWriteOffs();
        fetchAvailableItems();
      } catch (error) {
        console.error('Error deleting write-off:', error);
        setError(t('writeOffs.failedDelete'));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');

      const isTool = formData.itemKind === 'Tool';
      const isMachine = formData.itemKind === 'Machine';
      const submitData = {
        itemType: isTool ? 'TOOL' : 'MATERIAL_EQUIPMENT',
        toolId: isTool ? parseInt(formData.toolId, 10) : null,
        materialEquipmentId: isTool ? null : parseInt(formData.materialEquipmentId, 10),
        quantity: (isTool || isMachine) ? 1 : Number(formData.quantity || 0),
        reason: formData.reason,
        writeOffDate: formData.writeOffDate,
        notes: formData.notes,
      };

      if (editingWriteOff) {
        await updateWriteOff(editingWriteOff.id, submitData);
      } else {
        await createWriteOff(submitData);
      }

      handleClose();
      fetchWriteOffs();
      fetchAvailableItems();
    } catch (error) {
      console.error('Error saving write-off:', error);
      setError(error.response?.data?.error || t('writeOffs.failedSave'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'itemKind' && {
        toolId: '',
        materialEquipmentId: '',
        quantity: (value === 'Tool' || value === 'Machine') ? '1' : prev.quantity,
      })
    }));
  };

  const getItemOptions = () => {
    if (formData.itemKind === 'Tool') {
      return availableTools.map((tool) => ({
        value: tool.id,
        label: tool.serialNumber ? `${tool.name} (${tool.serialNumber})` : tool.name,
      }));
    }
    if (['Material', 'Machine', 'Equipment'].includes(formData.itemKind)) {
      const stockLabel = (item) => {
        const qty = warehouseStockQty(item);
        const unit = item.unit || 'kom';
        return `${item.name} — ${t('writeOffs.stockInWarehouse', { qty, unit })}`;
      };
      const rows = availableMaterialEquipment.filter((item) => item.type === formData.itemKind);
      const editingItem = editingWriteOff?.materialEquipment;
      if (
        editingItem
        && editingItem.type === formData.itemKind
        && !rows.some((item) => Number(item.id) === Number(editingItem.id))
      ) {
        rows.unshift(editingItem);
      }
      return rows.map((item) => ({
        value: item.id,
        label: stockLabel(item),
      }));
    }
    return [];
  };

  const isStockItem = ['Material', 'Equipment'].includes(formData.itemKind);
  const selectedStockItem = availableMaterialEquipment.find(
    (item) => String(item.id) === String(formData.materialEquipmentId)
  );

  return (
    <ResponsivePageSection sx={{ p: { xs: 0, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
        <Typography variant="h4" component="h1">
          {t('writeOffs.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpen}
        >
          {t('writeOffs.add')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isMobile ? (
        <ResponsiveDataList
          rows={writeOffs}
          getTitle={(writeOff) => (writeOff.tool?.name || writeOff.materialEquipment?.name || 'N/A')}
          getSubtitle={(writeOff) => `${itemKindLabel(writeOffItemKind(writeOff), t)} - ${formatDateEuropean(writeOff.writeOffDate)}`}
          renderFields={(writeOff) => (
            <Stack spacing={0.5}>
              <Typography variant="body2"><b>{t('writeOffs.reason')}:</b> {writeOff.reason}</Typography>
              <Typography variant="body2"><b>{t('writeOffs.writeOffBy')}:</b> {writeOff.writeOffBy}</Typography>
            </Stack>
          )}
          renderActions={(writeOff) => (
            <>
              <Button size="small" onClick={() => handleView(writeOff)}>{t('common.details')}</Button>
              <Button size="small" onClick={() => handleEdit(writeOff)}>{t('common.edit')}</Button>
              <Button size="small" color="error" onClick={() => handleDelete(writeOff.id)}>{t('common.delete')}</Button>
            </>
          )}
        />
      ) : (
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={writeOffs}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            loading={loading}
            disableSelectionOnClick
            getRowId={(row) => row.id}
          />
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          {editingWriteOff ? t('writeOffs.edit') : t('writeOffs.add')}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>{t('writeOffs.itemType')}</InputLabel>
                <Select
                  name="itemKind"
                  value={formData.itemKind}
                  onChange={handleChange}
                  label={t('writeOffs.itemType')}
                  required
                >
                  {ITEM_KINDS.map((kind) => (
                    <MenuItem key={kind} value={kind}>{itemKindLabel(kind, t)}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>{t('writeOffs.item')}</InputLabel>
                <Select
                  name={formData.itemKind === 'Tool' ? 'toolId' : 'materialEquipmentId'}
                  value={formData.itemKind === 'Tool' ? formData.toolId : formData.materialEquipmentId}
                  onChange={handleChange}
                  label={t('writeOffs.item')}
                  required
                  disabled={!formData.itemKind}
                >
                  {getItemOptions().map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                name="quantity"
                label={t('writeOffs.quantity')}
                value={formData.quantity}
                onChange={handleChange}
                fullWidth
                type="number"
                inputProps={{ min: 0.01, step: 0.01 }}
                required={isStockItem}
                disabled={!isStockItem}
                helperText={
                  selectedStockItem
                    ? t('writeOffs.stockInWarehouse', {
                      qty: warehouseStockQty(selectedStockItem),
                      unit: selectedStockItem.unit || 'kom',
                    })
                    : undefined
                }
              />

              <TextField
                name="reason"
                label={t('writeOffs.reason')}
                value={formData.reason}
                onChange={handleChange}
                fullWidth
                required
                multiline
                rows={3}
              />

              <DateInputWithArrows
                name="writeOffDate"
                label={t('writeOffs.writeOffDate')}
                value={formData.writeOffDate}
                onChange={handleChange}
                fullWidth
                required
              />

              <TextField
                name="notes"
                label={t('personnel.fields.notes')}
                value={formData.notes}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {editingWriteOff ? t('common.save') : t('common.create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewOpen} onClose={handleViewClose} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>{t('writeOffs.view')}</DialogTitle>
        <DialogContent>
          {selectedWriteOff && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <Typography>
                <strong>{t('writeOffs.itemType')}:</strong>
                <Chip 
                  label={itemKindLabel(writeOffItemKind(selectedWriteOff), t)} 
                  color={writeOffItemKind(selectedWriteOff) === 'Tool' ? 'primary' : 'secondary'}
                  size="small"
                  sx={{ ml: 1 }}
                />
              </Typography>
              <Typography>
                <strong>{t('writeOffs.item')}:</strong> {
                  selectedWriteOff.tool ? selectedWriteOff.tool.name : 
                  selectedWriteOff.materialEquipment ? selectedWriteOff.materialEquipment.name : 'N/A'
                }
              </Typography>
              <Typography><strong>{t('writeOffs.reason')}:</strong> {selectedWriteOff.reason}</Typography>
              <Typography><strong>{t('writeOffs.writeOffBy')}:</strong> {selectedWriteOff.writeOffBy}</Typography>
              <Typography>
                <strong>{t('writeOffs.writeOffDate')}:</strong> {formatDateEuropean(selectedWriteOff.writeOffDate)}
              </Typography>
              {selectedWriteOff.notes && (
                <Typography sx={{ gridColumn: '1 / -1' }}>
                  <strong>{t('personnel.fields.notes')}:</strong> {selectedWriteOff.notes}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleViewClose}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </ResponsivePageSection>
  );
}

export default WriteOffs;

